import {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import classnames from 'classnames'

import { ConnectedHelpTooltip } from '../../../../@paco/connectors'
import { ConceptDayComment, DayComment } from '../../../../@paco/entities/Comment/Comment'
import trans from '../../../../@paco/helpers/trans'
import { TimeModeType } from '../../../../@paco/types'

import { BaseScheduleShiftViewModel } from '../../../../entities/BaseScheduleShift/BaseScheduleShift'
import { ShiftConceptViewModelOld } from '../../../../entities/ShiftConcept/ShiftConcept'
import {
    UnavailableToWorkTimeSlotViewModel,
} from '../../../../entities/UnavailableToWorkTimeSlot/UnavailableToWorkTimeSlot'
import { scrollToElementAndCenter } from '../../../../helpers'
import useKeyPress from '../../../../helpers/hooks/useKeypress'
import {
    AbsenceViewModel,
    DragState,
    LeaveOfAbsenceViewModel,
    ShiftDraggable,
    ShiftType,
    ShiftViewModel,
    SpecialDayViewModel,
} from '../../../../models'
import transformShiftsToTransposedShiftDraggable
    from '../../../../services/ShiftConceptService/transformShiftsToTransposedShiftDraggable'
import transformShiftToShiftDraggable from '../../../../services/ShiftConceptService/transformShiftToShiftDraggable'
import ConceptDays from '../../components/ConceptDays/ConceptDaysProps'
import didUserClickShiftOrModalOrDeleteButton from './helpers/didUserClickShiftOrModalOrDeleteButton'
import getCrossWeekPreviewShifts from './helpers/getCrossWeekPreviewShifts'
import getMultiSelectedShiftIds from './helpers/getMultiSelectedShiftIds'
import getShiftsByIdAndShiftType from './helpers/getShiftsByIdAndShiftType'

import './ConceptsCalendar.scss'

type CalendarShift = ShiftViewModel | ShiftConceptViewModelOld | BaseScheduleShiftViewModel

interface ConceptsCalendarProps {
    canEditShifts?: boolean;
    canEditShiftConcepts?: boolean;
    canEditBaseScheduleShifts?: boolean;
    className?: string;
    showAddButton?: boolean;
    showAddConceptButton?: boolean;
    showDayInMonth?: boolean;
    dayHeaderFormat?: 'EEEEEE' | 'EEEE';
    hasDarkBackground?: boolean;
    hasMultiSelect?: boolean;
    showEmploymentTypeBadge?: boolean;
    showOverlappingShiftsWarnings?: boolean;
    deleteButtonId?: string;
    highlightShiftId?: string;
    isMobile?: boolean;
    timeMode?: TimeModeType;
    shifts: CalendarShift[];
    dragShifts?: CalendarShift[];
    specialDays?: SpecialDayViewModel[];
    leaveOfAbsences?: LeaveOfAbsenceViewModel[];
    absences?: AbsenceViewModel[];
    unavailableTimeSlots?: UnavailableToWorkTimeSlotViewModel[];
    selectedDays: Date[];
    comments?: DayComment[] | ConceptDayComment[];
    draggingShiftId?: string | null;
    selectedCopyDate?: Date | null;
    selectedShiftIds?: string[];
    onDraggingShiftIdChange?: (draggingShiftId: string | null) => void;
    onSelectedCopyDateChange?: (selectedCopyDate: Date | null) => void;
    onShiftClick: (shift: ShiftDraggable) => void;
    onAddButtonClick?: (day: Date) => void;
    onAddBaseScheduleButtonClick?: (day: Date) => void;
    onSelectedShiftsChange?: (shifts: string[]) => void;
    onDeleteShifts?: (shifts: string[]) => void;
    onCopyShifts?: (shifts: string[], dateOffset: number) => void;
    onCopyShiftsToDate?: (shifts: string[], targetDate: Date) => void;
    onAddComment?: (commentDate: Date) => void;
    onDeleteComment?: (commentId: string) => void;
}

const ConceptsCalendar: FC<ConceptsCalendarProps> = ({
    className,
    showAddButton = false,
    showAddConceptButton = false,
    showDayInMonth = false,
    hasDarkBackground = false,
    hasMultiSelect = false,
    canEditShifts = false,
    canEditShiftConcepts = false,
    canEditBaseScheduleShifts = false,
    showEmploymentTypeBadge = false,
    showOverlappingShiftsWarnings = false,
    dayHeaderFormat = 'EEEEEE',
    deleteButtonId,
    highlightShiftId,
    isMobile,
    timeMode,
    shifts,
    dragShifts,
    selectedDays,
    specialDays = [],
    leaveOfAbsences = [],
    unavailableTimeSlots = [],
    absences = [],
    comments,
    draggingShiftId: draggingShiftIdProp,
    selectedCopyDate: selectedCopyDateProp,
    selectedShiftIds: selectedShiftIdsProp,
    onDraggingShiftIdChange,
    onSelectedCopyDateChange,
    onShiftClick,
    onSelectedShiftsChange,
    onAddButtonClick,
    onAddBaseScheduleButtonClick,
    onDeleteShifts,
    onCopyShifts,
    onCopyShiftsToDate,
    onAddComment,
    onDeleteComment,
}) => {
    const [internalSelectedShiftIds, setInternalSelectedShiftIds] = useState<string[]>([])
    const [internalDraggingShiftId, setInternalDraggingShiftId] = useState<string | null>(null)
    const [internalSelectedCopyDate, setInternalSelectedCopyDate] = useState<Date | null>(null)

    const isSelectedShiftIdsControlled = selectedShiftIdsProp !== undefined
    const isDraggingShiftIdControlled = onDraggingShiftIdChange !== undefined
    const isSelectedCopyDateControlled = onSelectedCopyDateChange !== undefined

    const selectedShiftIds = isSelectedShiftIdsControlled ? selectedShiftIdsProp : internalSelectedShiftIds
    const draggingShiftId = isDraggingShiftIdControlled ? draggingShiftIdProp ?? null : internalDraggingShiftId
    const selectedCopyDate = isSelectedCopyDateControlled ? selectedCopyDateProp ?? null : internalSelectedCopyDate

    const shiftsForDrag = dragShifts ?? shifts

    const updateSelectedShiftIds = (ids: string[]) => {
        if (isSelectedShiftIdsControlled) {
            onSelectedShiftsChange?.(ids)
            return
        }

        setInternalSelectedShiftIds(ids)
    }

    const updateDraggingShiftId = (id: string | null) => {
        if (isDraggingShiftIdControlled) {
            onDraggingShiftIdChange?.(id)
            return
        }

        setInternalDraggingShiftId(id)
    }

    const updateSelectedCopyDate = (date: Date | null) => {
        if (isSelectedCopyDateControlled) {
            onSelectedCopyDateChange?.(date)
            return
        }

        setInternalSelectedCopyDate(date)
    }

    const shiftPress = useKeyPress(16)
    const leftCtrlPress = useKeyPress(17)
    const leftAltPress = useKeyPress(18)
    const cmdPress = useKeyPress(93)
    const singleSelectModeIsActive = hasMultiSelect && (leftCtrlPress || leftAltPress || cmdPress)
    const multiSelectModeIsActive = hasMultiSelect && shiftPress

    const classNames = useMemo(() => classnames('concepts-calendar', {
        'concepts-calendar--has-dark-background': hasDarkBackground,
        'concepts-calendar--has-single-select-mode-active': singleSelectModeIsActive,
        'concepts-calendar--is-dragging': !! draggingShiftId,
    }, className), [
        className,
        hasDarkBackground,
        draggingShiftId,
        leftCtrlPress,
        leftAltPress,
        cmdPress,
    ])

    const draggableShifts = useMemo(() => {
        const solidShifts = shifts.map((shift) => {
            const isSelected = selectedShiftIds.includes(shift.id)

            return transformShiftToShiftDraggable(
                shift,
                isSelected ? DragState.selected : DragState.neutral,
            )
        })

        if (! draggingShiftId || ! selectedCopyDate) {
            return solidShifts
        }

        const selectedShiftsForPreview = shiftsForDrag
            .filter((shift) => selectedShiftIds.includes(shift.id))
        const isForeignWeekDrag = ! shifts.some(shift => shift.id === draggingShiftId)

        if (isForeignWeekDrag) {
            return [
                ...solidShifts,
                ...getCrossWeekPreviewShifts(
                    selectedShiftsForPreview,
                    selectedCopyDate,
                    selectedDays,
                ),
            ]
        }

        const [previewShifts] = transformShiftsToTransposedShiftDraggable(
            draggingShiftId,
            selectedShiftsForPreview,
            selectedCopyDate,
            selectedDays,
            new Date(),
        )

        return [...solidShifts, ...previewShifts]
    }, [
        shifts,
        shiftsForDrag,
        draggingShiftId,
        selectedShiftIds,
        selectedDays,
        selectedCopyDate,
    ])

    const clearSelections = () => {
        updateDraggingShiftId(null)
        updateSelectedCopyDate(null)
        updateSelectedShiftIds([])
    }

    const toggleSelectedShiftId = (shiftId: string, shiftType: ShiftType) => {
        if (selectedShiftIds.includes(shiftId)) {
            updateSelectedShiftIds(selectedShiftIds.filter(id => id !== shiftId))
        } else if (hasMultiSelect) {
            const ids = getShiftsByIdAndShiftType(selectedShiftIds, shifts, shiftType)
            updateSelectedShiftIds([...ids, shiftId])
        } else {
            updateSelectedShiftIds([shiftId])
        }
    }

    const handleDayDragEnter = (date?: Date) => {
        updateSelectedCopyDate(date || null)
    }

    const handleShiftClick = (shift: ShiftDraggable) => {
        if (! singleSelectModeIsActive && ! multiSelectModeIsActive) {
            onShiftClick(shift)
        }

        if (multiSelectModeIsActive && shift.type === ShiftType.concept) {
            const newIds = getMultiSelectedShiftIds(
                selectedShiftIds,
                shift.id,
                shifts,
            )
            updateSelectedShiftIds(newIds)
        }

        if (singleSelectModeIsActive && shift.type === ShiftType.concept) {
            toggleSelectedShiftId(shift.id, shift.type)
        }
    }

    const handleShiftStartDrag = (shift: ShiftDraggable) => {
        updateDraggingShiftId(shift.id)

        if (! hasMultiSelect || shift.type !== ShiftType.concept) {
            updateSelectedShiftIds([shift.id])
            return
        }

        if (! selectedShiftIds.includes(shift.id)) {
            const ids = getShiftsByIdAndShiftType(selectedShiftIds, shifts, shift.type)
            updateSelectedShiftIds([...ids, shift.id])
        }
    }

    const handleDayDrop = () => {
        if (onCopyShiftsToDate) {
            if (selectedCopyDate) {
                onCopyShiftsToDate(selectedShiftIds, selectedCopyDate)
            }

            updateDraggingShiftId(null)
            updateSelectedCopyDate(null)

            return
        }

        if (! onCopyShifts) {
            return
        }

        const selectedShifts = shiftsForDrag.filter((shift) => selectedShiftIds.includes(shift.id))
        const [previewShiftConcepts, dateOffset] = transformShiftsToTransposedShiftDraggable(
            draggingShiftId,
            selectedShifts,
            selectedCopyDate,
            selectedDays,
            new Date(),
        )

        if (previewShiftConcepts.length) {
            onCopyShifts(previewShiftConcepts.map(shift => shift.id), dateOffset)
        }

        updateDraggingShiftId(null)
    }

    const handleAddButtonClick = (date: Date) => {
        clearSelections()
        if (onAddButtonClick) {
            onAddButtonClick(date)
        }
    }

    const handleAddConceptButtonClick = (date: Date) => {
        clearSelections()
        if (onAddBaseScheduleButtonClick) {
            onAddBaseScheduleButtonClick(date)
        }
    }

    const handleWindowClick = useCallback((event: any) => {
        if (! didUserClickShiftOrModalOrDeleteButton(event, deleteButtonId)) {
            clearSelections()
        }
    }, [
        deleteButtonId,
        isDraggingShiftIdControlled,
        isSelectedCopyDateControlled,
        isSelectedShiftIdsControlled,
        onDraggingShiftIdChange,
        onSelectedCopyDateChange,
        onSelectedShiftsChange,
    ])

    const handleDeleteButtonDrop = useCallback((shiftIdsToDelete: string[]) => {
        if (onDeleteShifts) {
            onDeleteShifts(shiftIdsToDelete)
        }
    }, [onDeleteShifts])

    const handleDeleteButtonDragEnter = useCallback(() => {
        updateSelectedCopyDate(null)
    }, [isSelectedCopyDateControlled, onSelectedCopyDateChange])

    useEffect(() => {
        clearSelections()
    }, [shifts.length])

    useEffect(() => {
        window.addEventListener('click', handleWindowClick)
        return () => {
            window.removeEventListener('click', handleWindowClick)
        }
    }, [handleWindowClick])

    useEffect(() => {
        const deleteButton = document.getElementById(deleteButtonId || '')

        if (deleteButton) {
            deleteButton.ondrop = () => handleDeleteButtonDrop(selectedShiftIds)
            deleteButton.ondragenter = handleDeleteButtonDragEnter
        }
    }, [deleteButtonId, selectedShiftIds, handleDeleteButtonDrop, handleDeleteButtonDragEnter])

    useEffect(() => {
        updateSelectedCopyDate(null)

        if (isSelectedShiftIdsControlled) {
            return
        }

        onSelectedShiftsChange?.(selectedShiftIds)
    }, [selectedShiftIds])

    useEffect(() => {
        const element = document.getElementById(`concept-shift-${highlightShiftId}`)
        if (element) {
            scrollToElementAndCenter(element)
        }
    }, [highlightShiftId])

    return (
        <div className={classNames}>
            <ConceptDays
                className="concepts-calendar__days"
                dayClassName="concepts-calendar__day"
                dayButtonsClassName="concepts-calendar__day-buttons"
                dayHeaderClassName="concepts-calendar__day-header"
                dayContentClassName="concepts-calendar__day-content"
                dayContentHeaderLabelClassName="concepts-calendar__day-content-header-label"
                dayContentHeaderDayClassName="concepts-calendar__day-content-header-day"
                shiftClassName="concepts-calendar__concept-shift"
                isDragging={!! draggingShiftId}
                isMobile={isMobile}
                canEditShifts={canEditShifts}
                canEditShiftConcepts={canEditShiftConcepts}
                canEditBaseScheduleShifts={canEditBaseScheduleShifts}
                showAddButton={showAddButton}
                showAddConceptButton={showAddConceptButton}
                showDayInMonth={showDayInMonth}
                showEmploymentTypeBadge={showEmploymentTypeBadge}
                showOverlappingShiftsWarnings={showOverlappingShiftsWarnings}
                timeMode={timeMode}
                dayHeaderFormat={dayHeaderFormat}
                highlightShiftId={highlightShiftId}
                shifts={draggableShifts}
                specialDays={specialDays}
                selectedDays={selectedDays}
                leaveOfAbsences={leaveOfAbsences}
                absences={absences}
                unavailableTimeSlots={unavailableTimeSlots}
                comments={comments}
                onAddButtonClick={handleAddButtonClick}
                onAddBaseScheduleButtonClick={handleAddConceptButtonClick}
                onShiftClick={handleShiftClick}
                onDayDragEnter={handleDayDragEnter}
                onDayDrop={handleDayDrop}
                onShiftStartDrag={handleShiftStartDrag}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
            />
            <ConnectedHelpTooltip
                index={0}
                route="calendar"
                subTitle={trans('help.conceptsCalendar.addBaseSchedule.title')}
                text={trans('help.conceptsCalendar.addBaseSchedule.text')}
                title={trans('help.conceptsCalendar.title')}
                showMobileInfoWarning
                className="concepts-calendar__add-base-schedule-help-tooltip"
            />
            <ConnectedHelpTooltip
                index={1}
                route="calendar"
                subTitle={trans('help.conceptsCalendar.addConceptShift.title')}
                text={trans('help.conceptsCalendar.addConceptShift.text')}
                title={trans('help.conceptsCalendar.title')}
                className="concepts-calendar__add-concept-shift-help-tooltip"
            />
            <ConnectedHelpTooltip
                index={2}
                route="calendar"
                subTitle={trans('help.conceptsCalendar.filters.title')}
                text={trans('help.conceptsCalendar.filters.text')}
                title={trans('help.conceptsCalendar.title')}
                showMobileInfoWarning
                className="shifts-calendar__filters-help-tooltip"
            />
        </div>
    )
}

export default ConceptsCalendar
